import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppUser } from '../../../../models/AppUser';
import { useAppUser } from '../../../../hooks/useAppUser';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { AppUserService } from '../../../../services/appUserService';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import Loading from '../../../../components/Loading';
import { Avatar } from '../../../../components/Avatar';
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import dateFormat from 'dateformat';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../../components/Dialog';
import { AppAnnouncement } from '../../../../models/AppAnnouncement';
import { useAppAnnouncements } from '../../../../hooks/useAppAnnouncement';
import { Text } from '../../../../components/Text';

const ViewAppAnnouncementIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [announcement, setAnnouncement] = useState<AppAnnouncement | null>(null);
    const navigate = useNavigate();
    const { fetchAnnouncementById, loading, error, } = useAppAnnouncements();

    useEffect(() => {
        document.title = "View App Announcement";
    }, []);

    useEffect(() => {
        if (!id) return;

        const fetchAnnouncementDetails = async () => {
            try {
                const fetchedAnnouncement = await fetchAnnouncementById(Number(id));
                setAnnouncement(fetchedAnnouncement!);
            } catch (err) {
                console.error('Failed to fetch user details:', err);
            }
        };

        fetchAnnouncementDetails();
    }, [id]);

    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;
    if (!announcement) return <p className="py-16 text-center">Announcement not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/app/announcements" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    App Announcements
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex items-center gap-4">
                    <Avatar square initials={announcement.title[0]} className="bg-zinc-300 text-gray-700 size-8" />
                    <Heading>{announcement.title}</Heading>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(announcement.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <Button onClick={() => navigate(`/gp/app/announcements/edit/${announcement.id}`)}>Edit</Button>
                    </div>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>Announcement Details</Subheading>
                <DescriptionList className="mt-4">

                    <DescriptionTerm>Title</DescriptionTerm>
                    <DescriptionDetails>{announcement.title || '-'}</DescriptionDetails>
                    <DescriptionTerm>Content</DescriptionTerm>
                    <DescriptionDetails>{announcement.content || '-'}</DescriptionDetails>

                    <DescriptionTerm>Files</DescriptionTerm>
                    <DescriptionDetails>
                        {announcement.image_url || announcement.image_url_2 || announcement.image_url_3 || announcement.image_url_4 || announcement.image_url_5 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {announcement.image_url && (
                                    <div className="space-y-2">
                                        {announcement.image_url.toLowerCase().endsWith('.pdf') ? (
                                            <div
                                                className="w-full h-48 bg-purple-50 border-2 border-purple-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                                onClick={() => announcement.image_url && window.open(announcement.image_url.startsWith('http') ? announcement.image_url : `${SERVER_URL}${announcement.image_url}`, '_blank')}
                                            >
                                                <svg className="w-16 h-16 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-sm text-purple-700 font-medium">Click to view PDF</Text>
                                            </div>
                                        ) : (
                                            <img
                                                src={announcement.image_url.startsWith('http') ? announcement.image_url : `${SERVER_URL}${announcement.image_url}`}
                                                alt={`${announcement.title} - Main file`}
                                                className="w-full h-48 object-cover rounded-lg border border-zinc-200 hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => announcement.image_url && window.open(announcement.image_url.startsWith('http') ? announcement.image_url : `${SERVER_URL}${announcement.image_url}`, '_blank')}
                                            />
                                        )}
                                        <div className="flex items-center justify-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url.toLowerCase().endsWith('.pdf')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {announcement.image_url.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                                            </span>
                                            <Text className="text-sm text-gray-600">Main File</Text>
                                        </div>
                                    </div>
                                )}
                                {announcement.image_url_2 && (
                                    <div className="space-y-2">
                                        {announcement.image_url_2.toLowerCase().endsWith('.pdf') ? (
                                            <div
                                                className="w-full h-48 bg-purple-50 border-2 border-purple-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                                onClick={() => announcement.image_url_2 && window.open(announcement.image_url_2.startsWith('http') ? announcement.image_url_2 : `${SERVER_URL}${announcement.image_url_2}`, '_blank')}
                                            >
                                                <svg className="w-16 h-16 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-sm text-purple-700 font-medium">Click to view PDF</Text>
                                            </div>
                                        ) : (
                                            <img
                                                src={announcement.image_url_2.startsWith('http') ? announcement.image_url_2 : `${SERVER_URL}${announcement.image_url_2}`}
                                                alt={`${announcement.title} - File 2`}
                                                className="w-full h-48 object-cover rounded-lg border border-zinc-200 hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => announcement.image_url_2 && window.open(announcement.image_url_2.startsWith('http') ? announcement.image_url_2 : `${SERVER_URL}${announcement.image_url_2}`, '_blank')}
                                            />
                                        )}
                                        <div className="flex items-center justify-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_2.toLowerCase().endsWith('.pdf')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {announcement.image_url_2.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                                            </span>
                                            <Text className="text-sm text-gray-600">File 2</Text>
                                        </div>
                                    </div>
                                )}
                                {announcement.image_url_3 && (
                                    <div className="space-y-2">
                                        {announcement.image_url_3.toLowerCase().endsWith('.pdf') ? (
                                            <div
                                                className="w-full h-48 bg-purple-50 border-2 border-purple-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                                onClick={() => announcement.image_url_3 && window.open(announcement.image_url_3.startsWith('http') ? announcement.image_url_3 : `${SERVER_URL}${announcement.image_url_3}`, '_blank')}
                                            >
                                                <svg className="w-16 h-16 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-sm text-purple-700 font-medium">Click to view PDF</Text>
                                            </div>
                                        ) : (
                                            <img
                                                src={announcement.image_url_3.startsWith('http') ? announcement.image_url_3 : `${SERVER_URL}${announcement.image_url_3}`}
                                                alt={`${announcement.title} - File 3`}
                                                className="w-full h-48 object-cover rounded-lg border border-zinc-200 hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => announcement.image_url_3 && window.open(announcement.image_url_3.startsWith('http') ? announcement.image_url_3 : `${SERVER_URL}${announcement.image_url_3}`, '_blank')}
                                            />
                                        )}
                                        <div className="flex items-center justify-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_3.toLowerCase().endsWith('.pdf')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {announcement.image_url_3.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                                            </span>
                                            <Text className="text-sm text-gray-600">File 3</Text>
                                        </div>
                                    </div>
                                )}
                                {announcement.image_url_4 && (
                                    <div className="space-y-2">
                                        {announcement.image_url_4.toLowerCase().endsWith('.pdf') ? (
                                            <div
                                                className="w-full h-48 bg-purple-50 border-2 border-purple-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                                onClick={() => announcement.image_url_4 && window.open(announcement.image_url_4.startsWith('http') ? announcement.image_url_4 : `${SERVER_URL}${announcement.image_url_4}`, '_blank')}
                                            >
                                                <svg className="w-16 h-16 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-sm text-purple-700 font-medium">Click to view PDF</Text>
                                            </div>
                                        ) : (
                                            <img
                                                src={announcement.image_url_4.startsWith('http') ? announcement.image_url_4 : `${SERVER_URL}${announcement.image_url_4}`}
                                                alt={`${announcement.title} - File 4`}
                                                className="w-full h-48 object-cover rounded-lg border border-zinc-200 hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => announcement.image_url_4 && window.open(announcement.image_url_4.startsWith('http') ? announcement.image_url_4 : `${SERVER_URL}${announcement.image_url_4}`, '_blank')}
                                            />
                                        )}
                                        <div className="flex items-center justify-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_4.toLowerCase().endsWith('.pdf')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {announcement.image_url_4.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                                            </span>
                                            <Text className="text-sm text-gray-600">File 4</Text>
                                        </div>
                                    </div>
                                )}
                                {announcement.image_url_5 && (
                                    <div className="space-y-2">
                                        {announcement.image_url_5.toLowerCase().endsWith('.pdf') ? (
                                            <div
                                                className="w-full h-48 bg-purple-50 border-2 border-purple-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                                onClick={() => announcement.image_url_5 && window.open(announcement.image_url_5.startsWith('http') ? announcement.image_url_5 : `${SERVER_URL}${announcement.image_url_5}`, '_blank')}
                                            >
                                                <svg className="w-16 h-16 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-sm text-purple-700 font-medium">Click to view PDF</Text>
                                            </div>
                                        ) : (
                                            <img
                                                src={announcement.image_url_5.startsWith('http') ? announcement.image_url_5 : `${SERVER_URL}${announcement.image_url_5}`}
                                                alt={`${announcement.title} - File 5`}
                                                className="w-full h-48 object-cover rounded-lg border border-zinc-200 hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => announcement.image_url_5 && window.open(announcement.image_url_5.startsWith('http') ? announcement.image_url_5 : `${SERVER_URL}${announcement.image_url_5}`, '_blank')}
                                            />
                                        )}
                                        <div className="flex items-center justify-center space-x-2">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_5.toLowerCase().endsWith('.pdf')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {announcement.image_url_5.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                                            </span>
                                            <Text className="text-sm text-gray-600">File 5</Text>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="text-gray-500">No files uploaded</span>
                        )}
                    </DescriptionDetails>

                </DescriptionList>
            </Card>
        </>
    );
};

export default ViewAppAnnouncementIdPage;
